/**
 * RGBA Generator
 *
 * This function will add opacity to a hex color and return the rgba value.
 *
 * Useful so that we can quickly update opacity based colors should the base color change in the future.
 *
 * @param hex
 * @param opacity
 * @returns {string}
 */
export const rbgaGenerator = (hex: string, opacity: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r},${g},${b},${opacity})`;
};

// Gold Colors
export const GOLD_LIGHTER = "#F4D2A0";
export const GOLD_LIGHT = "#E0AB6F";
export const GOLD_PRIMARY = "#B98141";
export const GOLD_DARK = "#9A601D";

// Neutral Colors
export const BLACK = "#000000";
export const WHITE = "#FFFFFF";

// Beige Colors
export const BEIGE_50 = "#F7F7F6";
export const BEIGE_100 = "#F4F3ED";

// Grey Colors
export const GREY_50 = "#F5F5F5";
export const GREY_100 = "#E6E6E6";
export const GREY_200 = "#D0D0D0";
export const GREY_300 = "#B2B2B2";
export const GREY_400 = "#6E6E6E";
export const GREY_500 = "#5A5A5A";
export const GREY_600 = "#3C3C3C";
export const GREY_700 = "#1A1A1A";

// Accent Colors
export const NAVY = "#213B58";
export const OLIVE = "#B6B8AC";
export const SAND = "#EBE2CC";
export const CHARCOAL = "#202020";
export const CANVAS = "#E5E3DC";

// Alert Colors
export const CRITICAL_100 = "#FEF2F2";
export const CRITICAL_400 = "#FF6161";
export const CRITICAL_500 = "#D60000";
export const CRITICAL_700 = "#4D0000";
export const POSITIVE_100 = "#F2FCF6";
export const POSITIVE_400 = "#00C851";
export const POSITIVE_500 = "#008033";
export const POSITIVE_700 = "#00481D";

// Campaign Colors
export const TCS_PURPLE = "#6402A2";
export const HOLIDAY_RED = "#E00000";
export const TMOBILE_PINK = "#ED008C";

// Alpha Colors
export const GOLD_PRIMARY_ALPHA_300 = rbgaGenerator(GOLD_PRIMARY, 0.16);
export const GOLD_PRIMARY_ALPHA_400 = rbgaGenerator(GOLD_PRIMARY, 0.28);
export const BLACK_ALPHA_100 = rbgaGenerator(BLACK, 0.04);
export const BLACK_ALPHA_200 = rbgaGenerator(BLACK, 0.08);
export const BLACK_ALPHA_300 = rbgaGenerator(BLACK, 0.16);
export const BLACK_ALPHA_400 = rbgaGenerator(BLACK, 0.24);
export const BLACK_ALPHA_500 = rbgaGenerator(BLACK, 0.36);
export const BLACK_ALPHA_600 = rbgaGenerator(BLACK, 0.48);
export const BLACK_ALPHA_700 = rbgaGenerator(BLACK, 0.64);
export const BLACK_ALPHA_800 = rbgaGenerator(BLACK, 0.8);
export const BLACK_ALPHA_900 = rbgaGenerator(BLACK, 0.92);
export const WHITE_ALPHA_100 = rbgaGenerator(WHITE, 0.04);
export const WHITE_ALPHA_200 = rbgaGenerator(WHITE, 0.08);
export const WHITE_ALPHA_300 = rbgaGenerator(WHITE, 0.16);
export const WHITE_ALPHA_400 = rbgaGenerator(WHITE, 0.24);
export const WHITE_ALPHA_500 = rbgaGenerator(WHITE, 0.36);
export const WHITE_ALPHA_600 = rbgaGenerator(WHITE, 0.48);
export const WHITE_ALPHA_700 = rbgaGenerator(WHITE, 0.64);
export const WHITE_ALPHA_800 = rbgaGenerator(WHITE, 0.8);
export const WHITE_ALPHA_900 = rbgaGenerator(WHITE, 0.92);

// Gradients
export const GOLD_FOIL_RIGHT = `linear-gradient(90deg, ${GOLD_DARK} 0%, ${GOLD_LIGHTER} 100%)`;
export const GOLD_FOIL_BOTTOM = `linear-gradient(0deg, ${GOLD_LIGHTER} 0%, ${GOLD_DARK} 100%)`;
export const SLATE_TOP = `linear-gradient(0deg, ${GREY_700} 0%, ${GREY_600} 100%)`;
export const GRADIENT_BLACK_OVERLAY = `linear-gradient(0deg, ${BLACK} 0%, transparent 100%)`;
export const GRADIENT_SLATE_DIAGONAL = `linear-gradient(225deg, ${GREY_700} 0%, ${GREY_600} 50%, ${GREY_700} 100%)`;
export const GRADIENT_SILVER_DIAGONAL = `linear-gradient(45deg, ${GREY_200} 0%, ${GREY_100} 100%)`;
export const GRADIENT_GOLD_DIAGONAL = `linear-gradient(90deg, ${GOLD_DARK} 0%, ${GOLD_PRIMARY} 100%)`;
export const GOLD_CHARCOAL_GRADIENT = `linear-gradient(90deg, ${GOLD_DARK} 0%, ${CHARCOAL} 100%)`;
